import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  notFound,
  Outlet,
  redirect,
  useParams,
  useRouter,
} from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"

import type { ExtendedBusinessEntity } from "@repo/supabase/database-types"
import { Button } from "@repo/ui/components/ui/button"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { UserAvatar } from "~/modules/auth/components/user-avatar"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { BusinessEntitiesSearchList } from "~/modules/business-entities/components/business-entities-search-list"
import { getExtendedResourceBySlugQueryOptions } from "~/modules/resources/resources.queries"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/$resourceSlug",
)({
  component: RouteComponent,
  beforeLoad: async ({ location, params, search }) => {
    // Redirect to the overview route if this naked route is accessed directly
    if (location.pathname === `/resources/${params.resourceSlug}`) {
      throw redirect({
        to: "./overview",
        from: "/resources/$resourceSlug",
        params,
        search,
      })
    }
  },
})

function RouteComponent() {
  const { isResourceManagementOn } = useFeatureConfigs()

  if (!isResourceManagementOn) {
    throw notFound()
  }

  const isMobile = useIsMobile()
  const params = useParams({ from: Route.id })
  const router = useRouter()
  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )
  const { data: resource } = useSuspenseQuery(
    getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
  )

  if (!resource) {
    throw notFound()
  }

  const currentPath = router.state.location.pathname
  const activeTab = currentPath.split("/").pop()

  const selectedBusinessEntitySlug = router.state.location.search.be_slug as
    | string
    | undefined

  const onBack = () => {
    router.navigate({
      to: "/resources",
      search: (prev) => prev,
    })
  }

  const onSelectBusinessEntity = (
    businessEntity: ExtendedBusinessEntity | null,
  ) => {
    router.navigate({
      to: "/resources",
      search: (prev) => ({ ...prev, be_slug: businessEntity?.slug }),
    })
  }

  const onTabChange = (tab: string) => {
    router.navigate({ to: `../${tab}`, search: router.state.location.search })
  }

  return (
    <PagePanel title={resource.first_name}>
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile,
          })}
        >
          <BusinessEntitiesSearchList
            businessEntities={businessEntitiesData.businessEntities}
            onSelectBusinessEntity={onSelectBusinessEntity}
            selectedBusinessEntitySlug={selectedBusinessEntitySlug}
          />
        </div>

        <div className={cn("block flex-1 md:w-2/3 lg:w-3/4")}>
          <div className="glass-darker flex h-full flex-col rounded-lg">
            <div className="flex-1 overflow-auto">
              <Tabs
                defaultValue={activeTab}
                onValueChange={onTabChange}
                className="flex h-full flex-col rounded-lg"
              >
                <div className="flex items-center gap-4 border-white/10 border-b px-3 py-2.5">
                  <Button variant="ghost" size="sm" onClick={onBack}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <UserAvatar
                    className="h-10 w-10 rounded-full"
                    id={resource.id}
                    email={resource.email}
                    pictureUrl={resource.picture_url}
                    displayName={`${resource.first_name} ${resource.last_name}`}
                  />
                  <div>
                    <div className="text-xl">
                      {resource.first_name} {resource.last_name}
                    </div>
                    <p className="text-sm text-white/70">{resource.email}</p>
                  </div>
                  <div className="ml-auto">
                    <TabsList>
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="documents">Documents</TabsTrigger>
                      <TabsTrigger value="projects">Projects</TabsTrigger>
                      <TabsTrigger value="invoices">Invoices</TabsTrigger>
                    </TabsList>
                  </div>
                </div>
                <div className="flex-1 overflow-auto p-6">
                  <Outlet />
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </PagePanel>
  )
}
